import { useCallback, useEffect, useState } from 'react';
import {
  FileText, Check, Loader2, AlertTriangle, ArrowRight, Info,
} from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import CampoContrasena from '../ui/CampoContrasena';

// PREPARAR LA FACTURACIÓN.
//
// ES UNA LISTA DE PENDIENTES, NO UN DIAGNÓSTICO. Decirle a alguien «tu
// catálogo no está listo para facturar» no le sirve de nada. Decirle
// «faltan estas cuatro categorías, y entre ellas arrastran 280
// productos», con la casilla para escribir la clave AL LADO, convierte el
// trabajo en algo que se puede terminar — y ver terminado.
//
// LA CLAVE SE ESCRIBE AQUÍ MISMO, en la misma línea donde dice que falta.
// Mandar a otra pantalla a buscar la categoría es donde se abandona una
// tarea de cuarenta renglones.

export default function Facturacion() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const avisos = useAvisos();

  const cargar = useCallback(
    () => api.get('/fiscal/preparacion').then(setD)
      .catch((e) => setError(e.message)),
    [],
  );

  useEffect(() => { cargar(); }, [cargar]);

  async function guardarClave(cat, clave) {
    await api.patch(`/categorias/${cat.id}`, { clave_prod_serv: clave });
    avisos.exito(
      `Listo: ${cat.productos} producto(s) de «${cat.nombre}» ya tienen su `
      + 'clave del SAT.');
    cargar();
  }

  if (error) {
    return (
      <div className="m-4 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3
                      text-sm text-red-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }
  if (!d) {
    return (
      <p className="flex items-center gap-2 p-4 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Revisando tu catálogo…
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-4 max-w-3xl">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">
          Preparar la facturación
        </h1>
        <p className="text-gray-600">
          El SAT no acepta «Balatas cerámicas»: pide una clave de ocho dígitos
          por cada cosa que vendes. Aquí se ponen <strong>una vez por
          categoría</strong> y las heredan todos sus productos.
        </p>
      </header>

      <div className={`flex gap-3 rounded-xl border p-3 ${d.listo
        ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
        {d.listo
          ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-700" aria-hidden="true" />
          : <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />}
        <p className={d.listo ? 'text-green-900' : 'text-amber-900'}>{d.mensaje}</p>
      </div>

      <NotaExplicativa titulo="¿De dónde saco estas claves?">
        Se las pides a tu contador: es una tarde suya, una vez. Le basta con
        la lista de categorías de abajo. Si te da una sola para todo, ponla
        como <strong>clave por omisión</strong> y afina después — vale más una
        clave aproximada en todo que ninguna en nada.
      </NotaExplicativa>

      {(d.categorias_sin_clave || []).length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold text-gray-900">
            Categorías sin clave ({(d.categorias_sin_clave || []).length})
          </h2>
          {/* ORDENADAS POR CUÁNTOS PRODUCTOS ARRASTRAN: quien tiene media
              hora tiene que poder gastarla donde más rinde. */}
          <p className="text-sm text-gray-600">
            Empieza por arriba: son las que más productos arreglan de una vez.
          </p>
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {(d.categorias_sin_clave || []).map((c) => (
              <Pendiente key={c.id} cat={c} onGuardar={guardarClave} />
            ))}
          </ul>
        </section>
      )}

      {d.productos_sin_unidad > 0 && (
        <NotaExplicativa titulo={`${d.productos_sin_unidad} productos sin clave de unidad`}>
          <Ayuda id="facturacion.unidad_sat" /> Los que ya decían «PIEZA» o «KG»
          se tradujeron solos; estos usan una unidad que hay que decir en la
          ficha de cada producto.
        </NotaExplicativa>
      )}

      <Emisor />
    </div>
  );
}

// ---------------------------------------------------------- el emisor
// TODO ESTO SE HACE UNA VEZ, con el contador al lado, y luego no se
// vuelve a abrir. Por eso cada cosa dice para qué sirve aquí mismo: quien
// lo lee no va a volver mañana a ver si mejoró la explicación.
function Emisor() {
  const [e, setE] = useState(null);
  const [error, setError] = useState('');
  const avisos = useAvisos();

  const cargar = useCallback(
    () => api.get('/fiscal/emisor').then(setE).catch(() => {}),
    [],
  );
  useEffect(() => { cargar(); }, [cargar]);

  if (!e) return null;
  const cert = e.certificado;

  return (
    <section className="flex flex-col gap-4 border-t border-gray-200 pt-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Tu empresa ante el SAT</h2>
        <p className="text-gray-600">
          Los datos que van a salir impresos en cada factura. Están todos en
          tu <strong>Constancia de Situación Fiscal</strong>.
        </p>
      </div>

      {/* EN MODO DE PRÁCTICA NADA TIENE VALOR FISCAL, y eso se dice donde
          se ve, no en una nota al pie. */}
      {e.en_pruebas && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="font-semibold text-amber-900">Modo de práctica</p>
          <p className="text-sm text-amber-800">
            Puedes probarlo todo sin miedo: lo que se emita aquí{' '}
            <strong>no tiene valor fiscal</strong> y no le llega al SAT. TEGO
            no saldrá de este modo hasta que tengas tus datos, tu Sello Digital
            y tu proveedor de timbrado contratado.
          </p>
        </div>
      )}

      {e.aviso && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3
                        text-sm text-red-800">{e.aviso}</div>
      )}

      <DatosFiscales
        e={e} onGuardado={(msg) => { avisos.exito(msg); cargar(); }}
        onError={setError} />

      <div className="rounded-xl border border-gray-200 p-3">
        <h3 className="font-semibold text-gray-900">Tu Sello Digital</h3>
        {cert ? (
          <div className="mt-1 text-sm text-gray-700">
            <p>
              {/* SEG-4: sin el permiso de datos fiscales el número llega en null. */}
              Certificado <strong>{cert.numero ?? '—'}</strong> · {cert.rfc}
            </p>
            <p className={cert.dias_para_vencer <= 30
              ? 'text-red-700' : 'text-gray-600'}>
              Vence el {new Date(cert.vigente_hasta).toLocaleDateString('es-MX')}
              {cert.dias_para_vencer >= 0
                ? ` (faltan ${cert.dias_para_vencer} días)`
                : ' — ya venció'}
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await api.post('/fiscal/probar-sello', {});
                  avisos.exito(r.mensaje);
                } catch (err) { setError(err.message); }
              }}
              className="mt-2 inline-flex items-center gap-1 font-semibold
                         text-[var(--color-marca)]">
              <Check className="h-4 w-4" aria-hidden="true" />
              Probar que funciona
            </button>
          </div>
        ) : (
          <p className="mt-1 text-sm text-gray-600">
            Sin subir. Son dos archivos que te dio el SAT al
            tramitar tu Sello Digital: uno termina en <strong>.cer</strong> y
            otro en <strong>.key</strong>, más la contraseña que elegiste
            entonces.{' '}
            <strong>No es tu e.firma</strong> — esa no debe subirse a ningún
            sistema.
          </p>
        )}
        <SubirSello onListo={(msg) => { avisos.exito(msg); cargar(); }}
                    onError={setError} />
      </div>

      <Series series={e.series} onCambio={cargar} onError={setError} />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3
                        text-sm text-red-800">{error}</div>
      )}
    </section>
  );
}

function DatosFiscales({ e, onGuardado, onError }) {
  const [f, setF] = useState({
    rfc: e.rfc || '', razon_social: e.razon_social || '',
    regimen_fiscal: e.regimen_fiscal || '', codigo_postal: e.codigo_postal || '',
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (ev) => setF((s) => ({ ...s, [k]: ev.target.value }));

  const campos = [
    ['rfc', 'RFC', 'El de tu empresa. 12 caracteres si es persona moral, 13 si eres persona física.'],
    ['razon_social', 'Razón social', 'Escrita EXACTAMENTE como aparece en tu constancia: el SAT la compara letra por letra.'],
    ['regimen_fiscal', 'Régimen fiscal', 'Los tres dígitos de tu constancia: 601, 612, 626…'],
    ['codigo_postal', 'Código postal', 'El del lugar desde donde facturas: el de tu sucursal.'],
  ];

  return (
    <form
      className="grid gap-3 sm:grid-cols-2"
      onSubmit={async (ev) => {
        ev.preventDefault();
        setGuardando(true);
        try {
          const r = await api.put('/fiscal/emisor', f);
          onGuardado('Datos fiscales guardados.');
          return r;
        } catch (err) { onError(err.message); return null; } finally {
          setGuardando(false);
        }
      }}>
      {campos.map(([k, etiqueta, ayuda]) => (
        <label key={k} className="block">
          <span className="block text-sm font-semibold text-gray-700">{etiqueta}</span>
          <input value={f[k]} onChange={set(k)}
                 className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
          <span className="mt-1 block text-xs text-gray-500">{ayuda}</span>
        </label>
      ))}
      <div className="sm:col-span-2">
        <button type="submit" disabled={guardando}
                className="inline-flex items-center gap-2 rounded-xl
                           bg-[var(--color-marca)] px-4 py-2 font-semibold
                           text-white disabled:bg-gray-300">
          {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Guardar los datos de mi empresa
        </button>
      </div>
    </form>
  );
}

function SubirSello({ onListo, onError }) {
  const [cer, setCer] = useState(null);
  const [key, setKey] = useState(null);
  const [contrasena, setContrasena] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const listo = cer && key && contrasena;

  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-3">
      <label className="block text-sm">
        <span className="font-semibold text-gray-700">Archivo .cer</span>
        <input type="file" accept=".cer" className="mt-1 block w-full text-sm"
               onChange={(ev) => setCer(ev.target.files?.[0] || null)} />
      </label>
      <label className="block text-sm">
        <span className="font-semibold text-gray-700">Archivo .key</span>
        <input type="file" accept=".key" className="mt-1 block w-full text-sm"
               onChange={(ev) => setKey(ev.target.files?.[0] || null)} />
      </label>
      <label className="block text-sm">
        <span className="font-semibold text-gray-700">Contraseña de la llave</span>
        <CampoContrasena value={contrasena} autoComplete="off"
                         onChange={(ev) => setContrasena(ev.target.value)}
                         className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
        <span className="mt-1 block text-xs text-gray-500">
          La que elegiste al generar el sello en el portal del SAT.
        </span>
      </label>
      <div className="sm:col-span-3">
        <button
          type="button" disabled={!listo || subiendo}
          onClick={async () => {
            setSubiendo(true);
            try {
              const cuerpo = new FormData();
              cuerpo.append('cer', cer);
              cuerpo.append('llave', key);
              cuerpo.append('contrasena', contrasena);
              const r = await api.enviarFormulario('/fiscal/certificado', cuerpo);
              setContrasena('');
              onListo(r.mensaje);
            } catch (err) { onError(err.message); } finally { setSubiendo(false); }
          }}
          className="inline-flex items-center gap-2 rounded-xl
                     bg-[var(--color-marca)] px-4 py-2 font-semibold text-white
                     disabled:bg-gray-300">
          {subiendo && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Subir mi Sello Digital
        </button>
        {/* Botón apagado CON LA RAZÓN A LA VISTA. */}
        {!listo && (
          <span className="ml-3 text-sm text-gray-500">
            Faltan los dos archivos y la contraseña.
          </span>
        )}
      </div>
    </div>
  );
}

function Series({ series, onCambio, onError }) {
  const [serie, setSerie] = useState('A');
  const [folio, setFolio] = useState('1');

  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <h3 className="font-semibold text-gray-900">Serie y folio</h3>
      <p className="text-sm text-gray-600">
        Con qué número van a salir tus facturas. <strong>Si vienes de otro
        sistema, pon el folio donde te quedaste allá</strong>: empezar en 1
        te dejaría dos facturas con el mismo número.
      </p>
      {series.length > 0 && (
        <ul className="mt-2 text-sm text-gray-700">
          {series.map((s) => (
            <li key={s.id}>
              Serie <strong>{s.serie}</strong> · la siguiente será la{' '}
              <strong>{s.folio_siguiente}</strong>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="block font-semibold text-gray-700">Serie</span>
          <input value={serie} maxLength={25}
                 onChange={(ev) => setSerie(ev.target.value.toUpperCase())}
                 className="mt-1 w-24 rounded-lg border border-gray-300 px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="block font-semibold text-gray-700">Primer folio</span>
          <input value={folio} inputMode="numeric"
                 onChange={(ev) => setFolio(ev.target.value)}
                 className="mt-1 w-32 rounded-lg border border-gray-300 px-3 py-2" />
        </label>
        <button
          type="button"
          onClick={async () => {
            try {
              await api.post('/fiscal/series',
                             { serie, folio_siguiente: Number(folio) || 1 });
              onCambio();
            } catch (err) { onError(err.message); }
          }}
          className="rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold
                     text-white">
          Crear la serie
        </button>
      </div>
    </div>
  );
}

function Pendiente({ cat, onGuardar }) {
  const [clave, setClave] = useState('');
  const [guardando, setGuardando] = useState(false);
  // VALIDACIÓN EN VIVO, no al guardar: ocho dígitos o nada.
  const mal = clave && !/^\d{8}$/.test(clave.trim());
  const listo = /^\d{8}$/.test(clave.trim());

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2">
      <span className="font-medium text-gray-900">{cat.nombre}</span>
      <span className="text-sm text-gray-500">
        {cat.productos === 0
          ? 'sin productos todavía'
          : `${cat.productos} producto${cat.productos === 1 ? '' : 's'}`}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <label className="sr-only" htmlFor={`clave-${cat.id}`}>
          Clave del SAT para {cat.nombre}
        </label>
        <input
          id={`clave-${cat.id}`} inputMode="numeric" maxLength={8}
          value={clave} onChange={(e) => setClave(e.target.value)}
          placeholder="25170000"
          className={`w-28 rounded-lg border px-2 py-2 ${mal
            ? 'border-red-300' : 'border-gray-300'}`} />
        <button
          type="button" disabled={!listo || guardando}
          onClick={async () => {
            setGuardando(true);
            try { await onGuardar(cat, clave.trim()); } finally { setGuardando(false); }
          }}
          className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-marca)]
                     px-3 py-2 font-semibold text-white disabled:bg-gray-300">
          {guardando
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          Guardar
        </button>
      </span>
      {/* Botón apagado CON LA RAZÓN A LA VISTA, nunca en silencio. */}
      {mal && (
        <span className="w-full text-sm text-red-700">
          La clave del SAT son ocho dígitos, por ejemplo 25170000.
        </span>
      )}
    </li>
  );
}

export { FileText };
